/**
 * Detección de bots — homologada con `propi-web-nextjs`.
 *
 * POR QUÉ EXISTE ACÁ: los dos repos escriben al MISMO proyecto de Mixpanel. Si
 * la Next filtra bots y esta SPA no, cualquier funnel que cruce los dos mezcla
 * tráfico filtrado con tráfico sin filtrar y los números dejan de ser
 * comparables. Además acá viven los eventos de pagos y KYC, donde el ruido
 * cuesta más caro.
 *
 * DUPLICACIÓN INTENCIONAL: es una copia de
 * `propi-web-nextjs/src/lib/analytics/botDetection.ts`, sin dependencias (allá
 * importa `getDeviceType`; acá va inline). Igual que con el catálogo de eventos,
 * NO unificar en un paquete compartido sin pedirlo — ver CLAUDE.md.
 *
 * REGLA: si cambian las reglas o los umbrales, se actualizan LOS DOS repos en el
 * mismo cambio. La evidencia, el costo medido de cada regla y el gate de
 * revisión de R3 viven en `propi-web-nextjs/src/lib/analytics/BOT_FILTERING.md`.
 *
 * PRINCIPIOS:
 *   1. Se evalúa UNA vez por page-load y se cachea. Un humano que entra normal y
 *      después hace F11 no debe quedar bloqueado a mitad de sesión.
 *   2. Ante cualquier error NO se bloquea (fail-open). Perder un bot es barato;
 *      perder un usuario real no.
 */

export type BotReason =
  | "webdriver"
  | "headless-ua"
  | "viewport-equals-screen"
  | "square-screen"
  | "chrome-145";

// R1 — OJO con `bot`: va con `\b` porque hay marcas de teléfono que lo contienen
// (ej. `CUBOT_X30`, un Android real). `bot\/` cubre los crawlers clásicos que se
// identifican como `AlgoBot/1.0`, donde `\b` no matchea.
const BOT_UA =
  /HeadlessChrome|PhantomJS|Puppeteer|Playwright|Selenium|Lighthouse|crawler|spider|scrapy|\bbot\b|bot\//i;

// R4 (origen geográfico por timezone) EXISTIÓ Y SE ELIMINÓ el 20-ago-2026, en
// ambos repos. NO REINTRODUCIRLA sin volver a medir.
//
// Bloqueaba todo lo que cayera fuera de `America/*` + España. Se quitó porque
// bloquea por DÓNDE ESTÁ el usuario, no por una inconsistencia técnica: era la
// única del set que podía bloquear a un humano legítimo, y lo hacía (~103
// logueados en 90 días, 3 de ellos con lead completado desde Francia).
//
// LO QUE SE PERDIÓ: era la única regla que cortaba la operación de
// China/Singapur (~17.800 usuarios cada 90 días). R1/R2/R5 NO la detectan.
// Detalle en `propi-web-nextjs/src/lib/analytics/BOT_FILTERING.md`.

/**
 * R3 — DEUDA TÉCNICA DELIBERADA. Revisar el 30-sep-2026 junto con la Next.
 *
 * Al revisar: si el volumen de esta versión volvió al ruido de fondo, REMOVER la
 * regla. NO actualizar el número — si hace falta actualizarlo, es señal de que
 * R1/R2/R5 no están cubriendo y hay que atacar eso en vez de perseguir versiones.
 */
const BLOCKED_CHROME_VERSION = 145;

let cachedReasons: BotReason[] | undefined;

/** Inline (la Next lo importa de EventTrakingUtils; acá no existe ese módulo). */
function isDesktop(ua: string): boolean {
  if (/Mobi|Android/i.test(ua)) return false;
  if (/iPad|Tablet/i.test(ua)) return false;
  return true;
}

/** Versión mayor de Chrome, o null si no es Chrome (Edge y Opera también traen `Chrome/`). */
function chromeMajorVersion(ua: string): number | null {
  if (/Edg\/|OPR\/|SamsungBrowser/.test(ua)) return null;
  const match = ua.match(/Chrome\/(\d+)/);
  return match ? Number(match[1]) : null;
}

/** Corre las 4 reglas y devuelve TODAS las que matchean (no corta en la primera). */
function detect(): BotReason[] {
  const reasons: BotReason[] = [];
  const ua = navigator.userAgent || "";
  const desktop = isDesktop(ua);

  // R1 — automatización declarada. Permanente, falsos positivos nulos.
  if ((navigator as Navigator & { webdriver?: boolean }).webdriver === true) {
    reasons.push("webdriver");
  }
  if (BOT_UA.test(ua)) reasons.push("headless-ua");

  // R2 — viewport igual a pantalla. Un headless renderiza sin barra de
  // herramientas; un Chrome real maximizado en 1920x1080 da ~945 de alto.
  // SÓLO desktop: en mobile la barra se oculta al scrollear y la igualdad puede
  // darse de verdad.
  if (
    desktop &&
    window.innerHeight === window.screen.height &&
    window.innerWidth === window.screen.width
  ) {
    reasons.push("viewport-equals-screen");
  }

  // R5 — pantalla casi cuadrada. Nadie fabrica paneles 1:1. El margen contra
  // hardware real es amplio: el monitor legítimo más cercano es 5:4 (1,25) y el
  // plegable desplegado 0,82.
  if (window.screen.height > 0) {
    const ratio = window.screen.width / window.screen.height;
    if (ratio >= 0.95 && ratio <= 1.05) reasons.push("square-screen");
  }

  // R3 — parche temporal. Las tres condiciones en AND para no matar al usuario
  // real que sí esté en esa versión.
  if (desktop && !document.referrer && chromeMajorVersion(ua) === BLOCKED_CHROME_VERSION) {
    reasons.push("chrome-145");
  }

  return reasons;
}

/**
 * Razones por las que este cliente se considera bot. Vacío = tráfico legítimo.
 * Se evalúa una vez por page-load y se cachea (ver principio 1 del encabezado).
 */
export function getBotReasons(): BotReason[] {
  if (cachedReasons) return cachedReasons;
  if (typeof window === "undefined") return [];

  try {
    cachedReasons = detect();
  } catch (e) {
    // Fail-open: si la detección rompe, el usuario trackea normal. No tragamos
    // el error — se avisa para poder arreglarlo.
    console.error("[analytics] detección de bots falló, se deja pasar el tráfico:", e);
    cachedReasons = [];
  }
  return cachedReasons;
}

/** ¿Bloqueamos a este cliente? */
export function isBot(): boolean {
  return getBotReasons().length > 0;
}

/**
 * Señales en MODO OBSERVACIÓN — no bloquean nada. Se registran como super
 * properties, así viajan en todos los eventos, incluidos los
 * `window.mixpanel.track(...)` sueltos.
 *
 * PARA QUÉ: validar reglas candidatas con evidencia antes de convertirlas en
 * bloqueo. Como los bots detectados nunca inicializan el SDK, estas señales sólo
 * llegan de tráfico que YA pasó las 4 reglas — que es justo lo que queremos
 * medir: si algún bot se está colando.
 */
export function getBotSignals(): Record<string, number | boolean> {
  if (typeof window === "undefined") return {};

  try {
    const nav = navigator as Navigator & { deviceMemory?: number };
    return {
      // Todo Mac moderno es Retina (2). Un "Mac" en 1 es imposible.
      bot_dpr: window.devicePixelRatio,
      // Chrome desktop real trae plugins de PDF; headless suele venir en 0.
      bot_plugins: nav.plugins?.length ?? -1,
      // Chrome siempre la expone; ausente = no es Chrome real.
      bot_device_memory: nav.deviceMemory ?? -1,
      bot_cpu_cores: nav.hardwareConcurrency ?? -1,
      // Headless a menudo lo trae vacío.
      bot_languages: nav.languages?.length ?? -1,
      // UA de desktop con valores de touch = inconsistencia.
      bot_touch_points: nav.maxTouchPoints ?? -1,
      // UA dice Chrome pero el objeto no existe = spoofeado.
      bot_has_chrome_obj: typeof (window as Window & { chrome?: unknown }).chrome !== "undefined",
    };
  } catch (e) {
    console.error("[analytics] señales de bot fallaron:", e);
    return {};
  }
}
